import { Grid } from '@material-ui/core';
import { constricaoTipoOptions } from 'common/genericOptions';
import {
  BloqueioDocumentType,
  BloqueioRequest,
  BloqueioTipo,
  FuncaoParticipante,
  ConstricaoTipo,
  ParticipanteResponse,
  PosicaoResponse,
  RegistroStatus,
} from 'common/genericTypes';
import FinishedSign from 'components/finishedSign';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, Dropdown, MdSpace, NumberField, Section, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';

const CadastroBloqueioPorConstricao: React.FC = () => {
  const [custodiantes, setCustodiantes] = useState<Array<ParticipanteResponse>>([]);
  const [custodianteId, setCustodianteId] = useState<string>('');
  const [posicoesInvestidores, setPosicoesInvestidores] = useState<Array<PosicaoResponse>>([]);
  const [posicoesAtivos, setPosicoesAtivos] = useState<Array<PosicaoResponse>>([]);
  const [investidorId, setInvestidorId] = useState<string>('');
  const [contaDeposito, setContaDeposito] = useState<string>('');
  const [quantidade, setQuantidade] = useState<number>(0);
  const [instrumentoGuid, setInstrumentoGuid] = useState<string>('');
  const [quantidadeDisponivel, setQuantidadeDisponivel] = useState<number>(0);
  const [constricaoTipo, setConstricaoTipo] = useState<ConstricaoTipo>();
  const [justificativa, setJustificativa] = useState<string>('');
  const [anexos, setAnexos] = useState<Array<Attachment>>([]);

  const [informacoesDeBloqueioFulfilled, setInformacoesDeBloqueioFulfilled] = useState<boolean>(false);
  const [formFulfilled, setFormFulfilled] = useState<boolean>(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const {
    postBloqueio,
    putUploadBloqueioDocs,
    getPosicoes: getPosicoesApi,
  } = useSelector((state: RootState) => state.investidorApi);
  const { getParticipantes: getParticipantesApi } = useSelector((state: RootState) => state.participanteApi);

  const getParticipantes = useCallback(
    async (categoria: FuncaoParticipante): Promise<Array<ParticipanteResponse>> => {
      return await getParticipantesApi(
        dispatch,
        { funcao: categoria, status: RegistroStatus.Aprovado, suspenso: false },
        true
      );
    },
    [dispatch, getParticipantesApi]
  );

  const getPosicoes = useCallback(
    async (custodianteId: string, investidorId?: string): Promise<Array<PosicaoResponse>> => {
      return await getPosicoesApi(dispatch, { custodianteId, investidorId }, true);
    },
    [dispatch, getPosicoesApi]
  );

  const handleSubmit = async () => {
    const bloqueioRequest: BloqueioRequest = {
      bloqueioTipo: BloqueioTipo.Constricao,
      custodianteId,
      investidorId,
      instrumentoFinanceiroId: instrumentoGuid,
      quantidade,
      justificativa,
      constricaoTipo,
    };
    try {
      const response = await postBloqueio(dispatch, bloqueioRequest, true);
      if (response?.id && anexos?.length) {
        await putUploadBloqueioDocs(
          dispatch,
          response.id,
          BloqueioDocumentType.Bloqueio,
          anexos.filter((_) => _.file).map((_) => _.file!),
          true
        );
      }
      setFinished(true);
    } catch (error) {}
  };

  const handleRemoveClick = (id: string) => {
    const updatedAnexos = anexos.filter((anexo) => anexo.id !== id);
    setAnexos(updatedAnexos);
  };

  const handleAnexosChange = (atts: Attachment[]) => {
    const updatedAnexos = anexos.concat(atts);
    setAnexos(updatedAnexos);
  };

  useEffect(() => {
    getParticipantes(FuncaoParticipante.Custodiante)
      .then(setCustodiantes)
      .catch(() => {});
  }, [dispatch, getParticipantes, setCustodiantes]);

  useEffect(() => {
    if (!custodianteId) {
      setPosicoesInvestidores([]);
      setPosicoesAtivos([]);
      setInstrumentoGuid('');
    } else {
      getPosicoes(custodianteId, undefined)
        .then((result) => setPosicoesInvestidores(result.filter((_) => !!_.saldoDisponivel)))
        .catch(() => {});
    }
  }, [dispatch, getPosicoes, custodianteId]);

  useEffect(() => {
    const posicoesAtivos = posicoesInvestidores.filter((_) => _.investidor.id === investidorId);
    setPosicoesAtivos(posicoesAtivos);
  }, [investidorId, posicoesInvestidores]);

  useEffect(() => {
    const investidor = posicoesAtivos?.[0]?.investidor;
    setContaDeposito(investidor?.contaDeposito ?? '');
  }, [posicoesAtivos]);

  useEffect(() => {
    setQuantidadeDisponivel(
      posicoesAtivos.find((_) => _.instrumentoFinanceiro.instrumentoFinanceiroGuid === instrumentoGuid)?.saldoDisponivel ?? 0
    );
  }, [instrumentoGuid, posicoesAtivos, setQuantidadeDisponivel]);

  useEffect(() => {
    setFormFulfilled(informacoesDeBloqueioFulfilled && (!!anexos?.length || !!justificativa));
  }, [informacoesDeBloqueioFulfilled, anexos, setFormFulfilled, justificativa]);

  return (
    <>
      {finished ? (
        <FinishedSign
          description={'Cadastro de bloqueio concluído com sucesso'}
          redirectRoute={'/gerenciar/investidores'}
        />
      ) : (
        <>
          <Section title={'Informações do Bloqueio'} defaultGrid isFulfilled={setInformacoesDeBloqueioFulfilled}>
            <Dropdown
              label={'Custodiante objeto de Constrição'}
              value={custodianteId}
              options={custodiantes.map((_) => ({ value: _.id, description: _.razaoSocial }))}
              onChange={setCustodianteId}
              required
            />
            <Dropdown
              label={'CPF/CNPJ do Investidor objeto de Constrição'}
              value={investidorId}
              options={Array.from(new Set(posicoesInvestidores.map((_) => _.investidor.id))).map((id) => ({
                value: id,
                description: addCpfCnpjMask(
                  posicoesInvestidores.find((_) => _.investidor.id === id)?.investidor.cpfCnpj ?? ''
                ),
              }))}
              onChange={setInvestidorId}
              required
            />
            <TextField label={'Conta depósito'} value={contaDeposito} onChange={() => {}} disabled />
            <Dropdown
              label={'Código do Ativo'}
              value={instrumentoGuid}
              options={posicoesAtivos.map((_) => ({
                value: _.instrumentoFinanceiro.instrumentoFinanceiroGuid,
                description: _.instrumentoFinanceiro.codigo,
              }))}
              onChange={(value) => setInstrumentoGuid(value)}
              required
            />
          <NumberField
              label={'Quantidade'}
              value={quantidade}
              numberType={getQuantityType(posicoesAtivos.find((_) => _.instrumentoFinanceiro.instrumentoFinanceiroGuid === instrumentoGuid)?.instrumentoFinanceiro.tipoInstrumentoFinanceiro)}
              helperText={`Quantidade disponível: ${formatNumber(quantidadeDisponivel, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`}
              error={Boolean(error)} 

              onChange={(value) => {
                const newValue = Number(value);
                if (!isNaN(newValue)) {
                  if (newValue < 0) {
                    setError('O valor não pode ser negativo');
                  } else if (newValue > quantidadeDisponivel) {
                    setError(`O valor não pode ser maior que a quantidade disponível`);
                  } else {
                    setError(null);
                    setQuantidade(newValue);
                  }
                }
              }}
      
              errorMessage={
                error
                  ? `${error}.\nQuantidade disponível: ${formatNumber(quantidadeDisponivel, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`
                  : undefined
              }
              
              required
            />
            <Dropdown
              label={'Tipo de Constrição'}
              value={constricaoTipo ?? ''}
              options={constricaoTipoOptions}
              onChange={(value) => setConstricaoTipo(value as ConstricaoTipo)}
              required
            />
            <TextField
              label="Justificativa"
              value={justificativa}
              rows={5}
              onChange={setJustificativa}
              multiline
              fullWidth
              required
            />
          </Section>
          <Section title={'Anexos'}>
            <UploadDocs onChange={handleAnexosChange} onDelete={handleRemoveClick} multipleFiles={true} />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!formFulfilled}>
                {'Comandar Bloqueio'}
              </Button>
              <MdSpace />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default CadastroBloqueioPorConstricao;
